<template>
  <div class="tw:relative tw:w-full tw:h-full">
    <Checkbox
      v-if="!isMainImage && customProperties.isDeletable"
      :name="`select${id}`"
      label=""
      :value="isSelected"
      class="tw:absolute tw:top-[8px] tw:left-[8px] tw:z-[9]"
      @onChange="emit('selectImage', $event, id)"
    />

    <div
      v-if="!isMainImage && customProperties.isLowQuality"
      class="tw:absolute tw:left-[10px] tw:bottom-[10px] tw:z-10"
    >
      <UiTooltip tooltip-position="top" :label="$t('app_images_low_quality_hint')" class="tw:w-6">
        <WarningTriangle class="tw:size-6" />
      </UiTooltip>
    </div>

    <div v-if="!isMainImage" class="tw:absolute tw:right-[10px] tw:bottom-0 tw:z-10">
      <div
        class="tw:hover:opacity-80 tw:cursor-pointer tw:inline-flex tw:items-center tw:justify-center"
        @keydown.enter="emit('toggleMenu', id)"
        @click="emit('toggleMenu', id)"
        tabindex="0"
      >
        <ChevronRight class="tw:rotate-90 tw:fill-white tw:size-6" />
      </div>

      <BaseCard
        v-click-outside="closeMenu"
        v-if="openId === id"
        no-padding
        class="tw:absolute tw:right-[-15px] tw:top-[30px] tw:w-[208px] tw:z-20"
      >
        <ul class="tw:list-none tw:m-0 tw:p-0">
          <li
            v-if="customProperties.isMainImageable"
            class="tw:p-2 tw:cursor-pointer tw:hover:bg-blue-100 tw:rounded-t-xl"
            @keydown.enter="openSetMainImageModal(imageUrl)"
            @click="openSetMainImageModal(imageUrl)"
            tabindex="0"
          >
            {{ $t('app_images_set_as_main_image') }}
          </li>
          <hr v-if="customProperties.isMainImageable && customProperties.isDeletable" />
          <li
            v-if="customProperties.isDeletable"
            class="tw:p-2 tw:cursor-pointer tw:hover:bg-blue-100 tw:rounded-b-xl"
            @click="() => openDeleteModal(id)"
            @keydown.enter="() => openDeleteModal(id)"
            tabindex="0"
          >
            {{ $t('delete') }}
          </li>
        </ul>
      </BaseCard>
    </div>
    <div
      v-if="isMainImage"
      class="tw:text-sm tw:absolute tw:top-[10px] tw:right-[10px] tw:bg-black/50 tw:text-white tw:rounded tw:py-1 tw:px-2 tw:z-10"
    >
      {{ $t('app_images_main_image') }}
    </div>

    <div
      v-if="deleteItemIds.length > 0 && deleteItemIds.includes(id)"
      class="tw:absolute tw:inset-0 tw:bg-white/80 tw:rounded-xl tw:z-[15]"
    >
      <LaunchpadLoader class="tw:h-full tw:flex tw:items-center tw:justify-center" />
    </div>

    <div
      class="tw:w-full tw:h-full tw:cursor-zoom-in"
      @click="() => openImagesGallery(id)"
      @keydown.enter="() => openImagesGallery(id)"
      tabindex="0"
    >
      <img
        referrerPolicy="strict-origin-when-cross-origin"
        :src="imageUrl"
        alt="hotel-image"
        class="tw:w-full tw:h-[187px] tw:object-cover tw:rounded-xl tw:block tw:hover:opacity-95"
      />
    </div>
  </div>
</template>

<script setup>
import Checkbox from '@/components/base/inputs/Checkbox.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import WarningTriangle from '@/components/icons/WarningTriangle.svg?component'
import { usePropertyImages } from '@/components/propertyDetails/propertyImages/usePropertyImages.ts'

const { deleteItemIds } = usePropertyImages()
const emit = defineEmits([
  'toggleDeleteModal',
  'openImageGallery',
  'setOpenImage',
  'toggleMenu',
  'selectImage',
  'openSetMainImageModal',
])

const props = defineProps({
  images: Array,
  openId: {
    type: [Number, String, null],
    default: null,
  },
  id: {
    type: String,
    default: '',
  },
  imageUrl: {
    type: String,
    default: '',
  },
  image: {
    type: Object,
    required: true,
  },
  isMainImage: {
    type: Boolean,
    default: false,
  },
  context: {
    type: String,
    default: '',
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  customProperties: {
    type: Object,
    default: () => ({}),
  },
})

const closeMenu = () => {
  emit('toggleMenu', null)
}

const openDeleteModal = imageId => {
  emit('toggleDeleteModal', imageId)
  emit('toggleMenu', imageId)
}

const openSetMainImageModal = image => {
  emit('openSetMainImageModal', image)
  emit('toggleMenu', null)
}

const openImagesGallery = imageId => {
  const mainImageObj = props.images?.find(item => item.imageId === imageId) || props.image
  if (!mainImageObj) {
    return
  }
  emit('openImageGallery')
  emit('setOpenImage', mainImageObj)
}
</script>
